import { Component, Prop, State, Watch, Element, h, Host } from '@stencil/core';
import { uuidv4, PnButtonAppearance, PnButtonVariant } from '@/index';

/**
 * The open/closing of the dropdown is handled by the component itself.
 *
 * @nativeClick Use the `click` event to listen when the button is clicked.
 */
@Component({
  tag: 'pn-button-dropdown',
  styleUrl: 'pn-button-dropdown.scss',
})
export class PnButtonDropdown {
  id: string = `pn-button-${uuidv4()}`;
  focusableElements: Array<HTMLLinkElement | HTMLButtonElement | HTMLInputElement>;
  container: HTMLDivElement;

  dropdownButtonId: string = `${this.id}-dropdown`;
  dropdownContentId: string = `${this.id}-content`;

  @Element() hostElement: HTMLElement;

  @State() right: boolean = false;
  @State() top: boolean = false;

  /** The required label on the button. */
  @Prop() label!: string;
  /** The optional SVG content of the icon you want. */
  @Prop() icon?: string;
  /** Select between `light` and `warning`. */
  @Prop() appearance?: PnButtonAppearance = '';
  /** Select between `outlined` and `borderless`. */
  @Prop() variant?: PnButtonVariant = '';
  /** Smaller button. */
  @Prop() small: boolean = false;
  /** Tooltip (required for Icon Only). */
  @Prop() tooltip: string;
  /** Open/close the dropdown without user interaction. */
  @Prop({ mutable: true, reflect: true }) open: boolean = false;

  @Watch('open')
  openHandler() {
    this.toggleDropdown();

    if (this.open) {
      document.addEventListener('click', this.globalHandler);
      document.addEventListener('keyup', this.globalHandler);
    } else {
      document.removeEventListener('click', this.globalHandler);
      document.removeEventListener('keyup', this.globalHandler);
    }
  }

  componentDidLoad() {
    if (this.open) this.openHandler();
  }

  getRect(element: HTMLElement): DOMRect {
    return element.getBoundingClientRect();
  }

  globalHandler = (e: KeyboardEvent) => {
    // global events that we want to track to close the select, like "click outside" or tab out
    if (!this.hostElement.contains(e.target as Element) || e?.key === 'Escape') {
      this.open = false;
    }
  };

  toggleDropdown() {
    this.right = this.hostElement.offsetLeft > window.innerWidth / 2;

    requestAnimationFrame(() => {
      if (this.open) {
        this.container.style.transition = 'none';
        this.container.style.transform = 'none';
      }

      const containerRect = this.getRect(this.container);
      const hostElementRect = this.getRect(this.hostElement);

      // Calculate if dropdown should open upwards
      // if there is enough space above button
      const enoughSpaceAbove = hostElementRect.top > containerRect.height + window.innerWidth * 0.025;
      this.top =
        enoughSpaceAbove &&
        hostElementRect.top + hostElementRect.height + containerRect.height >
          window.innerHeight - window.innerWidth * 0.025;

      let xAxis: number = 0;

      if (containerRect.right > window.innerWidth || containerRect.x < 0) {
        // 0.025 because the max width of the element is 95vw and we want the same margin on both sides
        xAxis = containerRect.x + containerRect.width + window.innerWidth * 0.025 - window.innerWidth;
      }

      this.container.style.transform = '';

      requestAnimationFrame(() => {
        this.container.style.transition = '';
        this.container.style.transform = this.open ? `scale(1) translateX(-${xAxis}px)` : '';

        requestAnimationFrame(() => {
          this.hostElement.querySelector<HTMLDivElement>('.pn-button-dropdown').dataset.open = `${this.open}`;
        });
      });
    });
  }

  toggle = () => {
    this.open = !this.open;
  };

  render() {
    return (
      <Host>
        <div class="pn-button-dropdown">
          <pn-button
            label={this.label}
            buttonId={this.dropdownButtonId}
            class="pn-button-dropdown-label"
            appearance={this.appearance}
            variant={this.variant}
            small={this.small}
            icon={this.icon}
            iconOnly={!!this.tooltip && !!this.icon && !this.label}
            ariacontrols={this.dropdownContentId}
            ariahaspopup="true"
            ariaexpanded={this.open.toString()}
            tooltip={this.tooltip}
            tooltipUp={!this.top}
            onPnClick={this.toggle}
          />
          <div
            ref={el => (this.container = el)}
            id={this.dropdownContentId}
            class="pn-button-dropdown-container"
            role="region"
            aria-labelledby={this.dropdownButtonId}
            data-right={this.right}
            data-top={this.top}
          >
            <div class="pn-button-dropdown-content">
              <slot />
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
